import { Accommodation } from '../types';
import { imageUrl } from './images';

/**
 * Curated Vermont and Maine inns, resorts, farm stays, and boutique hotels
 * that pair naturally with a cheese-trail itinerary.
 *
 * Each entry has a verified public-facing website. Price ranges, amenities,
 * and pet policies are based on the property's own marketing at time of
 * writing and should be re-checked seasonally.
 *
 * `bookingUrl` points to the most canonical reservation page. The `stay22`
 * helper in utils/affiliate.ts wraps this in an affiliate URL when
 * `VITE_STAY22_AID` is set in the environment; otherwise the raw URL is used.
 */
export const ACCOMMODATIONS: Accommodation[] = [
  // ----------------------------------------------------------------
  // VERMONT
  // ----------------------------------------------------------------
  {
    id: 'inn-at-shelburne-farms',
    type: 'accommodation',
    verified: true,
    region: 'champlain-valley',
    name: 'The Inn at Shelburne Farms',
    description:
      'A 24-room country house hotel on the shore of Lake Champlain, built in 1899 as the Webb family summer estate and now operated by the Shelburne Farms non-profit. Every room is unique, with period furniture and views of either the lake or the working farm. Open seasonally (mid-May through mid-October). The restaurant leans heavily on the farm itself for ingredients, including its own cheddar.',
    address: '99 Inn Rd',
    city: 'Shelburne',
    state: 'VT',
    lat: 44.4015,
    lng: -73.2490,
    image: imageUrl('white-farmhouse-porch'),
    imageKey: 'white-farmhouse-porch',
    hours: 'Seasonal — typically mid-May through mid-October',
    website: 'https://shelburnefarms.org/visit-and-learn/stay-shelburne-farms',
    bookingUrl: 'https://shelburnefarms.org/visit-and-learn/stay-shelburne-farms',
    phone: '(802) 985-8498',
    highlights: ['Lake Champlain frontage', '1,400-acre working farm', 'Farm-to-table dining', 'No TVs, no A/C'],
    priceRange: '$$$',
    style: 'Country Inn',
    petFriendly: false,
    amenities: [
      'Farm-to-table restaurant',
      'Afternoon tea',
      'Walking trails',
      'Lake swimming',
      'Cheesery on site',
    ],
  },
  {
    id: 'twin-farms',
    type: 'accommodation',
    verified: true,
    region: 'mad-river-green-mountains',
    name: 'Twin Farms',
    description:
      'A 300-acre Relais & Châteaux all-inclusive estate in the hills outside Woodstock, with 10 individually designed cottages and a handful of rooms in the main house. Rates include every meal and beverage (the cellar holds tens of thousands of bottles), cross-country trails, a private pond, and spa access. Consistently rated among the top resorts in North America.',
    address: '452 Royalton Turnpike',
    city: 'Barnard',
    state: 'VT',
    lat: 43.7359,
    lng: -72.6144,
    image: imageUrl('cow-mountain-pasture'),
    imageKey: 'cow-mountain-pasture',
    hours: 'Year-round; reservations often months out',
    website: 'https://twinfarms.com/',
    bookingUrl: 'https://twinfarms.com/reservations',
    phone: '(802) 234-9999',
    highlights: ['All-inclusive', 'Relais & Châteaux', '10 private cottages', 'Spa + pond'],
    priceRange: '$$$$',
    style: 'Resort',
    petFriendly: false,
    amenities: [
      'All meals included',
      'Full bar included',
      'Spa',
      'Cross-country ski trails',
      'Private pond',
      'Cottage fireplaces',
    ],
  },
  {
    id: 'pitcher-inn',
    type: 'accommodation',
    verified: true,
    region: 'mad-river-green-mountains',
    name: 'The Pitcher Inn',
    description:
      'A Relais & Châteaux inn in Warren village with eleven guest rooms — nine in the main inn and two family-friendly two-bedroom suites in the adjacent Red Barn. Each room is designed around a Vermont theme (the Chester Arthur, the Trout, the Mountain Room), and the inn’s restaurant holds one of the strongest wine cellars in the state.',
    address: '275 Main St',
    city: 'Warren',
    state: 'VT',
    lat: 44.1128,
    lng: -72.8569,
    image: imageUrl('white-farmhouse-porch'),
    imageKey: 'white-farmhouse-porch',
    hours: 'Year-round, Mad River Valley',
    website: 'https://pitcherinn.com/',
    bookingUrl: 'https://pitcherinn.com/suites-guest-rooms/',
    phone: '(802) 496-6350',
    highlights: ['Relais & Châteaux', 'Themed rooms', 'Acclaimed wine cellar', 'Mad River Valley'],
    priceRange: '$$$$',
    style: 'Country Inn',
    petFriendly: false,
    amenities: ['Fine dining restaurant', 'Tavern', '26,000+ bottle cellar', 'Gourmet breakfast'],
  },
  {
    id: 'woodstock-inn',
    type: 'accommodation',
    verified: true,
    region: 'mad-river-green-mountains',
    name: 'Woodstock Inn & Resort',
    description:
      'A full-service resort on the Woodstock Green with 142 rooms, a spa, heated pool, 18-hole Robert Trent Jones golf course, and an athletic club. A classic New England base for exploring the Upper Valley, and a short drive to Sugarbush Farm and Billings Farm & Museum.',
    address: '14 The Green',
    city: 'Woodstock',
    state: 'VT',
    lat: 43.6237,
    lng: -72.5186,
    image: imageUrl('white-farmhouse-porch'),
    imageKey: 'white-farmhouse-porch',
    hours: 'Year-round',
    website: 'https://woodstockinn.com/',
    bookingUrl: 'https://woodstockinn.com/',
    phone: '(802) 457-1100',
    highlights: ['On the Woodstock Green', 'Spa + pool', 'Golf course', 'Full-service resort'],
    priceRange: '$$$$',
    style: 'Resort',
    petFriendly: false,
    amenities: ['Spa', 'Indoor pool', 'Robert Trent Jones golf', 'Fine dining', 'Athletic club'],
  },
  {
    id: 'liberty-hill-farm',
    type: 'accommodation',
    verified: true,
    region: 'mad-river-green-mountains',
    name: 'Liberty Hill Farm & Inn',
    description:
      'A working 400-acre Holstein dairy farm and seven-room B&B run by the Kennett family since 1979, at the edge of the Green Mountain National Forest. Guests help feed calves, visit the milking parlor, and gather for a family-style farm dinner each evening. One of the most authentic farm-stay experiences in New England.',
    address: '511 Liberty Hill Rd',
    city: 'Rochester',
    state: 'VT',
    lat: 43.8765,
    lng: -72.8234,
    image: imageUrl('dairy-cow-black-white'),
    imageKey: 'dairy-cow-black-white',
    hours: 'Year-round',
    website: 'https://libertyhillfarm.com/',
    bookingUrl: 'https://libertyhillfarm.com/',
    phone: '(802) 767-3926',
    highlights: ['Working dairy farm', 'Family-style dinner included', 'Calves + milking parlor', 'Green Mountain National Forest'],
    priceRange: '$$',
    style: 'Farm Stay',
    petFriendly: false,
    amenities: ['Farm breakfast + dinner included', 'Milking demonstrations', 'Hiking trails', 'Hay wagon rides'],
  },
  {
    id: 'hotel-vermont',
    type: 'accommodation',
    verified: true,
    region: 'champlain-valley',
    name: 'Hotel Vermont',
    description:
      'A 125-room independent boutique hotel on the Burlington waterfront, a short walk from Church Street Marketplace and Lake Champlain ferries. Lobby fireplace, locally sourced restaurant (Juniper), and a thoughtful Vermont-made minibar. Pet-friendly with no extra fees.',
    address: '41 Cherry St',
    city: 'Burlington',
    state: 'VT',
    lat: 44.4800,
    lng: -73.2161,
    image: imageUrl('lake-champlain-boats'),
    imageKey: 'lake-champlain-boats',
    hours: 'Year-round',
    website: 'https://hotelvt.com/',
    bookingUrl: 'https://hotelvt.com/',
    phone: '(802) 651-0080',
    highlights: ['Burlington waterfront', 'Walk to Church Street', 'Pet-friendly', 'Independent + local'],
    priceRange: '$$$',
    style: 'Boutique Hotel',
    petFriendly: true,
    amenities: ['Juniper Bar & Restaurant', 'Bike rentals', 'Fireside lounge', 'Vermont-made minibar'],
  },
  {
    id: 'willard-street-inn',
    type: 'accommodation',
    verified: true,
    region: 'champlain-valley',
    name: 'Willard Street Inn',
    description:
      'A fourteen-room B&B in a restored 1881 Queen Anne mansion in Burlington’s Hill Section, ten minutes on foot from the waterfront. Walking distance to Church Street and a short drive to Shelburne Farms. Breakfast included in the solarium each morning.',
    address: '349 S Willard St',
    city: 'Burlington',
    state: 'VT',
    lat: 44.4709,
    lng: -73.2050,
    image: imageUrl('lake-champlain-boats'),
    imageKey: 'lake-champlain-boats',
    hours: 'Year-round',
    website: 'https://willardstreetinn.com/',
    bookingUrl: 'https://willardstreetinn.com/',
    phone: '(802) 651-8710',
    highlights: ['1881 Queen Anne mansion', 'Walk to Church Street', 'Breakfast included'],
    priceRange: '$$$',
    style: 'B&B',
    petFriendly: false,
    amenities: ['Breakfast included', 'Gardens', 'Solarium', 'Period furnishings'],
  },
  {
    id: 'jackson-house-inn',
    type: 'accommodation',
    verified: true,
    region: 'southern-vermont',
    name: 'The Jackson House Inn',
    description:
      'An 1890 Colonial-Revival inn just west of Woodstock Village with nine guest rooms and suites on four acres of groomed gardens. A quiet alternative to the Woodstock Inn, walking distance to the Ottauquechee River and a short drive to Sugarbush Farm.',
    address: '114-3 Senior Ln',
    city: 'Woodstock',
    state: 'VT',
    lat: 43.6247,
    lng: -72.5420,
    image: imageUrl('white-farmhouse-porch'),
    imageKey: 'white-farmhouse-porch',
    hours: 'Year-round',
    website: 'https://jacksonhouse.com/',
    bookingUrl: 'https://jacksonhouse.com/',
    phone: '(802) 457-2065',
    highlights: ['1890 Colonial Revival', 'Four acres of gardens', 'Walk to the Ottauquechee'],
    priceRange: '$$$',
    style: 'B&B',
    petFriendly: false,
    amenities: ['Breakfast included', 'Gardens', 'Private baths', 'Sitting rooms'],
  },

  // ----------------------------------------------------------------
  // MAINE
  // ----------------------------------------------------------------
  {
    id: 'inn-by-the-sea',
    type: 'accommodation',
    verified: true,
    region: 'greater-portland-casco-bay',
    name: 'Inn by the Sea',
    description:
      'An oceanfront boutique resort on Crescent Beach in Cape Elizabeth, about fifteen minutes south of Portland and a short drive from Silvery Moon Creamery. Spacious suites, a spa, and an ocean-view restaurant (Sea Glass) that leans hard into Maine seafood and local dairy. Genuinely pet-friendly — the inn treats dogs as guests.',
    address: '40 Bowery Beach Rd',
    city: 'Cape Elizabeth',
    state: 'ME',
    lat: 43.5602,
    lng: -70.2090,
    image: imageUrl('lighthouse-cape-elizabeth'),
    imageKey: 'lighthouse-cape-elizabeth',
    hours: 'Year-round',
    website: 'https://innbythesea.com/',
    bookingUrl: 'https://innbythesea.com/',
    phone: '(207) 799-3134',
    highlights: ['Crescent Beach oceanfront', 'Pet-friendly', 'Spa + fireplace lounge', 'Sea Glass restaurant'],
    priceRange: '$$$$',
    style: 'Resort',
    petFriendly: true,
    amenities: ['Beachfront', 'Spa', 'Ocean-view dining', 'Outdoor pool (seasonal)', 'Dog amenities'],
  },
  {
    id: 'press-hotel-portland',
    type: 'accommodation',
    verified: true,
    region: 'greater-portland-casco-bay',
    name: 'The Press Hotel',
    description:
      'A 110-room boutique hotel in the former Portland Press Herald building in the Old Port, part of Marriott’s Autograph Collection. Journalism-themed design, a well-regarded restaurant (Inkwell), and an easy walking base for Portland’s restaurants and ferries to Casco Bay.',
    address: '119 Exchange St',
    city: 'Portland',
    state: 'ME',
    lat: 43.6585,
    lng: -70.2555,
    image: imageUrl('portland-cobblestone'),
    imageKey: 'portland-cobblestone',
    hours: 'Year-round',
    website: 'https://thepresshotel.com/',
    bookingUrl: 'https://thepresshotel.com/',
    phone: '(207) 808-8800',
    highlights: ['Old Port location', 'Autograph Collection', 'Historic newspaper building'],
    priceRange: '$$$',
    style: 'Boutique Hotel',
    petFriendly: true,
    amenities: ['Inkwell restaurant', 'Fitness center', 'Walk to ferries', 'Art gallery'],
  },
  {
    id: 'camden-harbour-inn',
    type: 'accommodation',
    verified: true,
    region: 'midcoast-maine',
    name: 'Camden Harbour Inn',
    description:
      'A Small Luxury Hotels of the World member perched above Camden Harbor with 20 individually decorated rooms and suites, most with harbor or mountain views. The on-site restaurant, Natalie’s, has held a Forbes Five-Star rating. A strong midcoast base close to Appleton Creamery.',
    address: '83 Bayview St',
    city: 'Camden',
    state: 'ME',
    lat: 44.2108,
    lng: -69.0606,
    image: imageUrl('lighthouse-rocky-coast'),
    imageKey: 'lighthouse-rocky-coast',
    hours: 'Year-round',
    website: 'https://camdenharbourinn.com/',
    bookingUrl: 'https://camdenharbourinn.com/',
    phone: '(207) 236-4200',
    highlights: ['Harbor-view perch', 'Small Luxury Hotels of the World', 'Natalie’s restaurant'],
    priceRange: '$$$$',
    style: 'Boutique Hotel',
    petFriendly: false,
    amenities: ['Fine dining', 'Harbor views', 'Full breakfast included', 'Concierge'],
  },
  {
    id: 'asticou-inn',
    type: 'accommodation',
    verified: true,
    region: 'acadia-mount-desert',
    name: 'Asticou Inn',
    description:
      'An 1883 shingle-style inn at the head of Northeast Harbor on Mount Desert Island, a five-minute drive from the gates of Acadia National Park and the Asticou Azalea Garden. Historic main-inn rooms plus adjacent cottages, with a dining room overlooking the harbor.',
    address: '15 Peabody Dr',
    city: 'Northeast Harbor',
    state: 'ME',
    lat: 44.2951,
    lng: -68.2870,
    image: imageUrl('lighthouse-cape-elizabeth'),
    imageKey: 'lighthouse-cape-elizabeth',
    hours: 'Seasonal — typically mid-May through October',
    website: 'https://asticou.com/',
    bookingUrl: 'https://asticou.com/',
    phone: '(207) 276-3344',
    highlights: ['1883 shingle-style inn', 'Near Acadia National Park', 'Harbor-front dining'],
    priceRange: '$$$',
    style: 'Country Inn',
    petFriendly: false,
    amenities: ['Harbor-view dining', 'Outdoor pool', 'Tennis courts', 'Azalea Garden nearby'],
  },

  // ------------------------------------------------------------------
  // NEW HAMPSHIRE
  // ------------------------------------------------------------------
  // Verified against each property's official site on 2026-04-19.
  // ------------------------------------------------------------------
  {
    id: 'sugar-hill-inn',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-19',
    region: 'white-mountains-nh',
    name: 'Sugar Hill Inn',
    description:
      'A boutique country inn on a quiet ridge in Sugar Hill with long views across the Franconia range. Owner-operated with an award-leaning prix-fixe dinner, a wine program, and rooms and cottages scattered across the property. About a mile from Harman\u2019s Cheese & Country Store \u2014 the logical base for a White Mountains cheese weekend.',
    address: '116 NH-117',
    city: 'Sugar Hill',
    state: 'NH',
    lat: 44.2045,
    lng: -71.8135,
    image: imageUrl('white-farmhouse-porch'),
    imageKey: 'white-farmhouse-porch',
    hours: 'Year-round',
    website: 'https://www.sugarhillinn.com/',
    bookingUrl: 'https://www.sugarhillinn.com/',
    phone: '(603) 823-5621',
    highlights: ['Franconia views', 'Walk to Harman\u2019s', 'Prix-fixe dinner'],
    priceRange: '$$$',
    style: 'Country Inn',
    petFriendly: false,
    amenities: ['Fine dining', 'Wine list', 'Mountain views', 'Cottages available'],
  },
  {
    id: 'manor-on-golden-pond',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-19',
    region: 'white-mountains-nh',
    name: 'The Manor on Golden Pond',
    description:
      'An early-1900s English-style manor house above Squam Lake \u2014 the lake that stood in for Golden Pond in the 1981 film. Rooms in the main house and separate cottages, a Forbes-recognized dining room, lake access, and a spa. Natural pairing with Sandwich Creamery\u2019s self-serve farmstand a short drive north into the Lakes Region.',
    address: '31 Manor Dr',
    city: 'Holderness',
    state: 'NH',
    lat: 43.7270,
    lng: -71.5930,
    image: imageUrl('lake-champlain-boats'),
    imageKey: 'lake-champlain-boats',
    hours: 'Year-round',
    website: 'https://www.manorongoldenpond.com/',
    bookingUrl: 'https://www.manorongoldenpond.com/',
    phone: '(603) 968-3348',
    highlights: ['Squam Lake views', 'Historic manor house', 'Fine dining'],
    priceRange: '$$$$',
    style: 'Country Inn',
    petFriendly: false,
    amenities: ['Lake access', 'Spa', 'Fine dining', 'Cottages available'],
  },
  {
    id: 'hanover-inn-dartmouth',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-19',
    region: 'white-mountains-nh',
    name: 'Hanover Inn Dartmouth',
    description:
      'A four-diamond boutique hotel on Dartmouth College\u2019s green in downtown Hanover, steps from the Hopkins Center for the Arts and the Hood Museum. PINE restaurant leans farm-to-table Upper Valley. A strong base for driving the Connecticut River valley \u2014 Robie Farm in Piermont is under an hour north.',
    address: '2 E Wheelock St',
    city: 'Hanover',
    state: 'NH',
    lat: 43.7025,
    lng: -72.2880,
    image: imageUrl('portland-cobblestone'),
    imageKey: 'portland-cobblestone',
    hours: 'Year-round',
    website: 'https://www.hanoverinn.com/',
    bookingUrl: 'https://www.hanoverinn.com/',
    phone: '(603) 643-4300',
    highlights: ['On the Dartmouth green', 'PINE restaurant', 'Upper Valley base'],
    priceRange: '$$$',
    style: 'Boutique Hotel',
    petFriendly: true,
    amenities: ['Farm-to-table dining', 'Walk to campus', 'Fitness center', 'Business center'],
  },
  {
    id: 'centennial-hotel-concord',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-19',
    region: 'merrimack-valley-nh',
    name: 'The Centennial Hotel',
    description:
      'A boutique hotel set in an 1890s red-brick Victorian a few blocks from the New Hampshire State House in Concord. Contemporary interiors, a well-regarded on-site restaurant, and an easy launchpad for the Merrimack Valley loop \u2014 Brookford Farm in Canterbury and Contoocook Creamery are each roughly fifteen minutes away.',
    address: '96 Pleasant St',
    city: 'Concord',
    state: 'NH',
    lat: 43.2060,
    lng: -71.5445,
    image: imageUrl('red-barn-silo'),
    imageKey: 'red-barn-silo',
    hours: 'Year-round',
    website: 'https://www.thecentennialhotel.com/',
    bookingUrl: 'https://www.thecentennialhotel.com/',
    phone: '(603) 227-9000',
    highlights: ['Victorian brick facade', 'Walk to State House', 'Capital-region base'],
    priceRange: '$$',
    style: 'Boutique Hotel',
    petFriendly: true,
    amenities: ['On-site restaurant', 'Historic building', 'Fitness center', 'Free parking'],
  },
];
